/**
 * ClaimStatusPill — индикатор состояния claim прямо на дашборде.
 *
 * Состояния:
 *   READY  — ручной claim готов. Золотая пульсирующая капсула,
 *            крупная цифра pending profit, тап → ClaimModal.
 *   WAIT   — идёт 24-часовой цикл генерации. Две строки:
 *            • "Ручной клейм · HH:MM:SS" — если есть non-PC слоты
 *            • "Автосбор PC · N · HH:MM:SS" — если есть PC слоты
 *            Если и то, и другое — показываем обе строки, так
 *            юзер сразу видит что оба процесса идут отдельно.
 */
import { memo, useEffect, useState } from 'react';
import { Zap } from 'lucide-react';

interface ClaimStatusPillProps {
  ready: boolean;
  pendingProfit: number;
  /** Секунд до ближайшего ручного claim (non-PC слот) */
  cycleSecondsLeft: number;
  /** Есть ли хоть один ручной (non-PC) слот */
  hasManualSlots: boolean;
  /** Время до ближайшего PC-автосбора (unix ms), null если нет PC */
  pcNextClaimMs: number | null;
  /** Сколько PC-слотов у юзера */
  pcContractsCount: number;
  onClick: () => void;
  /** Тап по ряду «АВТОСБОР PC» — открыть инфо-модалку */
  onPcInfoClick?: () => void;
}

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

function formatHHMMSS(secs: number, t: TFunction): string {
  const total = Math.max(0, Math.floor(secs));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return t('nodes.timeHMS2', {
    h: h.toString().padStart(2, '0'),
    m: m.toString().padStart(2, '0'),
    s: s.toString().padStart(2, '0'),
  });
}

function ClaimStatusPillImpl({
  ready,
  pendingProfit,
  cycleSecondsLeft,
  hasManualSlots,
  pcNextClaimMs,
  pcContractsCount,
  onClick,
  onPcInfoClick,
}: ClaimStatusPillProps) {
  const { t } = useTranslation();
  // Локальный 1-Hz тик для PC-таймера (у него абсолютный ms,
  // надо пересчитывать каждую секунду)
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // pcSecLeft = 0 означает "PC уже должен был сработать" (автосбор задержан
  // или NULL next_claim). В любом случае показываем ряд — пусть юзер
  // видит что PC-ноды есть и отслеживает их статус.
  const pcSecLeft = pcNextClaimMs && pcNextClaimMs > nowMs
    ? Math.max(0, Math.round((pcNextClaimMs - nowMs) / 1000))
    : 0;
  const showManual = hasManualSlots;
  const showPc = pcContractsCount > 0;
  const pcOverdue = showPc && pcSecLeft === 0;

  if (ready) {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onClick}
          className="w-full rounded-[var(--radius-pill)] py-3 px-4 flex items-center justify-between gap-3 cursor-pointer claim-pill-ready"
          style={{
            background:
              'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
            boxShadow:
              '0 16px 40px -10px color-mix(in oklab, var(--accent-primary) 70%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.55)',
            color: '#0a0a0f',
          }}
        >
          <span className="flex items-center gap-2">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#0a0a0f] opacity-50 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#0a0a0f]" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-extrabold">
              {t('nodes.statusReady')}
            </span>
          </span>
          <span className="flex items-center gap-1.5 font-display font-extrabold text-[16px] tracking-tight tabular-nums">
            <Zap size={14} strokeWidth={2.6} />
            +{pendingProfit.toFixed(3)} USDT
          </span>
        </button>
        {/* Параллельно с READY-кнопкой показываем PC-ряд — чтобы юзер
            видел что автосбор PC-нод тоже работает, а не думал, что
            9.878 это ВСЯ прибыль по всем нодам. */}
        {showPc && (
          <TimerRow
            label={t('nodes.pcAutoLabel', { count: pcContractsCount })}
            time={pcOverdue ? t('nodes.pcSoon') : formatHHMMSS(pcSecLeft, t)}
            accent="primary"
            onClick={onPcInfoClick}
          />
        )}
      </div>
    );
  }

  // Нет ни того, ни другого — пустая плашка (не рисуем ничего)
  if (!showManual && !showPc) return null;

  return (
    <div className="flex flex-col gap-2">
      {showManual && (
        <TimerRow
          label={t('nodes.manualClaimLabel')}
          time={formatHHMMSS(cycleSecondsLeft, t)}
          accent="secondary"
        />
      )}
      {showPc && (
        <TimerRow
          label={t('nodes.pcAutoLabel', { count: pcContractsCount })}
          time={pcOverdue ? t('nodes.pcSoon') : formatHHMMSS(pcSecLeft, t)}
          accent="primary"
          onClick={onPcInfoClick}
        />
      )}
    </div>
  );
}

function TimerRow({
  label,
  time,
  accent,
  onClick,
}: {
  label: string;
  time: string;
  accent: 'primary' | 'secondary';
  onClick?: () => void;
}) {
  const color = accent === 'primary' ? 'var(--accent-primary)' : 'var(--text-secondary)';
  const clickable = Boolean(onClick);
  return (
    <div
      role={clickable ? 'button' : undefined}
      onClick={onClick}
      className={`lg-pill !rounded-[var(--radius-pill)] py-2.5 px-4 flex items-center justify-between gap-3 opacity-90 ${clickable ? 'cursor-pointer active:opacity-70 transition-opacity' : ''}`}
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-bold">
        {label}
      </span>
      <span
        className="font-mono font-extrabold text-[13px] tabular-nums flex-shrink-0 inline-flex"
        style={{ color }}
      >
        {time.split('').map((ch, i) => (
          <span
            key={i}
            style={{
              display: 'inline-block',
              width: ch === ' ' ? '0.35em' : '0.62em',
              textAlign: 'center',
            }}
          >
            {ch}
          </span>
        ))}
      </span>
    </div>
  );
}

export const ClaimStatusPill = memo(ClaimStatusPillImpl);
